import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Upload, Eye, X, Info, Loader } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';
import { apiCall } from '../../services/apiUtil';
import { uploadFile } from '../../services/imageUploadService';
import toast from 'react-hot-toast';
import TermsBuilderModal from './TermsBuilderModal';
import DocumentViewerModal from './DocumentViewerModal';

function DocumentRow({ label, path, onView, onRemove, isDark, theme }) {
  // What the document IS, not where it is stored.
  //
  // This showed the storage filename — "terms-1786769256361.json" — which tells
  // an organiser nothing about their own terms and exposes a key they never
  // chose. The label was already passed in and thrown away.
  const fileName = path ? path.split('/').pop() : '';
  // The theme's own surface, not a light/dark guess. `isDark` describes the
  // APP's palette; inside an academy scope the surrounding page can be light
  // while that flag is still true, which painted a near-black card into the
  // middle of a pale form.
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${theme.border} ${theme.backgroundSecondary}`}>
      <div className="flex items-center gap-x-2 min-w-0">
        <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
        <span className="min-w-0">
          <span className={`block text-sm truncate ${theme.text}`}>{label}</span>
          <span className={`block text-xs ${theme.textMuted}`}>Saved — view or replace it</span>
        </span>
      </div>
      <div className="flex items-center gap-x-1 ml-3 flex-shrink-0">
        <button onClick={onView} title="View document"
          className="p-1.5 rounded hover:opacity-70 text-blue-500 transition-colors">
          <Eye className="w-3.5 h-3.5" />
        </button>
        <button onClick={onRemove} title="Remove document"
          className="p-1.5 rounded hover:opacity-70 text-red-400 transition-colors">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function DocumentActions({ type, tournamentId, tournamentName, onSaved, disabled, theme, isDark }) {
  const fileInputRef = useRef(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Only PDF files are allowed'); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error('File must be under 25 MB'); return; }

    setUploading(true);
    try {
      const entityPath = `${tournamentId}/${type}-${Date.now()}.pdf`;
      const result = await uploadFile(file, 'tournament_documents', file.name, entityPath);
      const key = result.key;

      // Save path to tournament
      await apiCall(`/tournaments/${tournamentId}`, {
        method: 'PATCH',
        body: JSON.stringify(type === 'terms'
          ? { termsContentPath: key }
          : { waiverContentPath: key }
        ),
      });

      onSaved(key);
      toast.success('Document uploaded successfully');
    } catch {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div>
      {showBuilder && (
        <TermsBuilderModal
          type={type}
          tournamentId={tournamentId}
          tournamentName={tournamentName}
          onSaved={onSaved}
          onClose={() => setShowBuilder(false)}
        />
      )}
      <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden" onChange={handlePdfUpload} />
      <div className="flex gap-x-2">
        <button
          type="button"
          onClick={() => setShowBuilder(true)}
          disabled={disabled}
          className={`${btn.base} ${btn.sm} ${theme.buttonPrimary} flex items-center gap-x-1.5`}
        >
          <FileText className="w-3.5 h-3.5" />
          <span>Create from Template</span>
        </button>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading}
          className={`${btn.base} ${btn.sm} ${theme.buttonSecondary} flex items-center gap-x-1.5`}
        >
          {uploading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          <span>{uploading ? 'Uploading...' : 'Upload PDF'}</span>
        </button>
      </div>
    </div>
  );
}

function DocumentSection({ docType, label, enabled, path, isEditing, tournamentId, tournamentName, onToggle, onSaved, onRemove, disabled, theme, isDark }) {
  const navigate = useNavigate();
  const [showViewer, setShowViewer] = useState(false);
  const isViewMode = !isEditing && !!tournamentId;  // existing tournament, not in edit mode
  const isCreateMode = !isEditing && !tournamentId; // new tournament being created

  const handleView = () => setShowViewer(true);

  // View mode — read-only display
  if (isViewMode) {
    return (
      <>
        {showViewer && (
          <DocumentViewerModal
            type={docType}
            tournamentId={tournamentId}
            onClose={() => setShowViewer(false)}
          />
        )}
        <div className={`flex items-center justify-between p-3 rounded-lg border ${theme.border}`}>
          <div className="flex items-center gap-x-2">
            <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <span className={`text-sm font-medium ${theme.text}`}>{label}</span>
          </div>
          {!enabled ? (
            <span className={`text-sm ${theme.textMuted} italic`}>Not opted</span>
          ) : path ? (
            <button
              type="button"
              onClick={handleView}
              className="flex items-center gap-x-1.5 text-sm text-blue-500 hover:text-blue-600 font-medium transition-colors"
            >
              <Eye className="w-3.5 h-3.5" />
              <span>View Document</span>
            </button>
          ) : (
            <span className={`text-sm ${theme.textMuted} italic`}>Not created yet</span>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      {showViewer && (
        <DocumentViewerModal
          type={docType}
          tournamentId={tournamentId}
          onClose={() => setShowViewer(false)}
        />
      )}
      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={e => onToggle(e.target.checked)}
            disabled={disabled}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className={`text-sm font-medium ${theme.text}`}>Require {label}</span>
        </label>

        {enabled && (
          <div className="ml-6">
            {isCreateMode ? (
              /* Create mode — document can only be added after tournament is created */
              <div className={`flex items-start gap-x-2 p-3 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border border-blue-200 dark:border-blue-800`}>
                <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  You can create or upload your {label} document after the tournament is created.
                </p>
              </div>
            ) : path ? (
              <>
              <DocumentRow
                label={label}
                path={path}
                onView={handleView}
                onRemove={onRemove}
                isDark={isDark}
                theme={theme}
              />
                {/* The document can still be rebuilt or replaced while one
                    exists. Offering only View and Remove meant an organiser had
                    to delete their terms — briefly leaving the tournament with
                    none — before they could correct a typo in them. */}
              <DocumentActions
                type={docType}
                tournamentId={tournamentId}
                tournamentName={tournamentName}
                onSaved={onSaved}
                disabled={disabled}
                theme={theme}
                isDark={isDark}
              />
              </>
            ) : (
              <DocumentActions
                type={docType}
                tournamentId={tournamentId}
                tournamentName={tournamentName}
                onSaved={onSaved}
                disabled={disabled}
                theme={theme}
                isDark={isDark}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default function TermsWaiverSection({ formData, setFormData, tournamentId, isEditing, disabled }) {
  const { theme } = useTheme();
  const isDark = theme.background !== 'bg-white';
  const tournamentName = formData.tournamentName || 'Tournament';
  const isViewMode = !isEditing && !!tournamentId;

  const update = (patch) => setFormData(prev => ({ ...prev, ...patch }));

  return (
    <div className={`${theme.backgroundSecondary} rounded-lg p-3 sm:p-4`}>
      <h3 className={`text-sm sm:text-base font-bold ${theme.text} mb-3 flex items-center`}>
        <FileText className="w-4 h-4 mr-2 text-blue-500" />
        Registration Requirements
      </h3>

      <div className="space-y-4">
        <DocumentSection
          docType="terms"
          label="Terms & Conditions"
          enabled={formData.requireTerms}
          path={formData.termsContentPath}
          isEditing={isEditing}
          tournamentId={tournamentId}
          tournamentName={tournamentName}
          onToggle={(checked) => update({ requireTerms: checked, termsContentPath: checked ? formData.termsContentPath : '' })}
          onSaved={(key) => update({ termsContentPath: key })}
          onRemove={() => update({ termsContentPath: '' })}
          disabled={disabled}
          theme={theme}
          isDark={isDark}
        />

        <DocumentSection
          docType="waiver"
          label="Liability Waiver"
          enabled={formData.requireWaiver}
          path={formData.waiverContentPath}
          isEditing={isEditing}
          tournamentId={tournamentId}
          tournamentName={tournamentName}
          onToggle={(checked) => update({ requireWaiver: checked, waiverContentPath: checked ? formData.waiverContentPath : '' })}
          onSaved={(key) => update({ waiverContentPath: key })}
          onRemove={() => update({ waiverContentPath: '' })}
          disabled={disabled}
          theme={theme}
          isDark={isDark}
        />
      </div>
    </div>
  );
}
